import React from 'react';
import { motion } from 'framer-motion';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { Code, Zap, Shield, Webhook, Key, ArrowRight } from 'lucide-react';
const products = [{
  name: 'Orchestrate',
  href: '/docs/api-reference/orchestrate',
  icon: Zap,
  desc: 'Intelligent routing and execution'
}, {
  name: 'Payments',
  href: '/docs/api-reference/payments',
  icon: Code,
  desc: 'Collections and payouts'
}, {
  name: 'Wallets',
  href: '/docs/api-reference/wallets',
  icon: Key,
  desc: 'Multi-currency balances'
}, {
  name: 'Exchange',
  href: '/docs/api-reference/exchange',
  icon: Code,
  desc: 'FX quotes and trades'
}, {
  name: 'Compliance',
  href: '/docs/api-reference/compliance',
  icon: Shield,
  desc: 'KYB/KYC and screening'
}, {
  name: 'Liquidity',
  href: '/docs/api-reference/liquidity',
  icon: Code,
  desc: 'Liquidity access and execution'
}, {
  name: 'Tokenize',
  href: '/docs/api-reference/tokenize',
  icon: Code,
  desc: 'Tokenized assets and operations'
}, {
  name: 'Connect',
  href: '/docs/api-reference/connect',
  icon: Webhook,
  desc: 'API keys and webhooks'
}];
const commonObjects = [{
  name: 'money',
  desc: 'Amount in minor units + currency code'
}, {
  name: 'address',
  desc: 'Structured address with country, postal code'
}, {
  name: 'beneficiary',
  desc: 'Payout destination with bank details'
}, {
  name: 'rail',
  desc: 'Payment rail identifier and capabilities'
}, {
  name: 'metadata',
  desc: 'Key-value pairs for custom data'
}];
export function APIReferenceOverviewPage() {
  const pingCode = {
    curl: `curl https://api.obseq.com/v1/ping \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    node: `const response = await fetch('https://api.obseq.com/v1/ping', {
  headers: { 'Authorization': \`Bearer \${apiKey}\` }
});
const data = await response.json();`,
    python: `import requests

r = requests.get(
    'https://api.obseq.com/v1/ping',
    headers={'Authorization': f'Bearer {api_key}'}
)
print(r.json())`,
    go: `req, _ := http.NewRequest("GET", "https://api.obseq.com/v1/ping", nil)
req.Header.Set("Authorization", "Bearer "+apiKey)
resp, _ := client.Do(req)`,
    java: `HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("https://api.obseq.com/v1/ping"))
    .header("Authorization", "Bearer " + apiKey)
    .GET()
    .build();`,
    php: `$response = $client->get('https://api.obseq.com/v1/ping', [
    'headers' => ['Authorization' => 'Bearer ' . $apiKey]
]);`,
    ruby: `response = HTTParty.get(
  'https://api.obseq.com/v1/ping',
  headers: { 'Authorization' => "Bearer #{api_key}" }
)`,
    dotnet: `var request = new HttpRequestMessage(HttpMethod.Get, 
    "https://api.obseq.com/v1/ping");
request.Headers.Add("Authorization", $"Bearer {apiKey}");`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'API Reference',
    href: '/docs/api-reference'
  }]} onThisPage={[{
    title: 'Quick Start',
    id: 'quick-start'
  }, {
    title: 'Global Conventions',
    id: 'conventions'
  }, {
    title: 'Product APIs',
    id: 'products'
  }, {
    title: 'Common Objects',
    id: 'common-objects'
  }]} data-id="element-1050">
      <h1 data-id="element-1051">API Reference</h1>
      <p className="lead" data-id="element-1052">
        Obseq API v1 is a REST JSON API spanning Orchestrate, Payments, Wallets,
        Exchange, Compliance, Liquidity, Tokenize, and Connect.
      </p>
      <p data-id="element-1053">
        Use this reference for endpoint details, request/response schemas, and
        code examples. For end-to-end workflows, see the{' '}
        <a href="#/docs/guides/getting-started" className="text-blue-600 hover:text-blue-700" data-id="element-1054">
          Guides
        </a>
        .
      </p>

      <h2 id="quick-start" data-id="element-1055">Make Your First Call</h2>
      <p data-id="element-1056">Test your API key with a simple ping request:</p>
      <CodeBlock code={pingCode} data-id="element-1057" />
      <p data-id="element-1058">Expected response:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto" data-id="element-1059">
        <code data-id="element-1060">{`{
  "status": "ok",
  "timestamp": "2026-01-03T18:41:22Z"
}`}</code>
      </pre>

      <h2 id="conventions" data-id="element-1061">Global Conventions</h2>
      <p data-id="element-1062">
        All Obseq APIs follow consistent patterns. See{' '}
        <a href="#/docs/api-conventions" className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-1063">
          API Conventions
        </a>{' '}
        for complete details.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6" data-id="element-1064">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1065">
          <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1066">Authentication</h4>
          <p className="text-sm text-slate-600 mb-2" data-id="element-1067">
            Bearer token required on all requests
          </p>
          <code className="text-xs bg-slate-900 text-slate-100 px-2 py-1 rounded" data-id="element-1068">
            Authorization: Bearer &lt;key&gt;
          </code>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1069">
          <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1070">Idempotency</h4>
          <p className="text-sm text-slate-600 mb-2" data-id="element-1071">
            Safe retries with idempotency keys
          </p>
          <code className="text-xs bg-slate-900 text-slate-100 px-2 py-1 rounded" data-id="element-1072">
            Idempotency-Key: &lt;uuid&gt;
          </code>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1073">
          <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1074">Pagination</h4>
          <p className="text-sm text-slate-600" data-id="element-1075">
            Cursor-based with limit, starting_after, ending_before
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1076">
          <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1077">Webhooks</h4>
          <p className="text-sm text-slate-600" data-id="element-1078">
            Real-time events with signature verification
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1079">
          <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1080">Errors</h4>
          <p className="text-sm text-slate-600" data-id="element-1081">
            Consistent error schema with type, code, message
          </p>
        </motion.div>
      </div>

      <h2 id="products" data-id="element-1082">Product APIs</h2>
      <p data-id="element-1083">Explore detailed endpoint documentation for each product:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8" data-id="element-1084">
        {products.map((product, index) => <motion.a key={product.href} href={`#${product.href}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05
      }} whileHover={{
        y: -4
      }} className="group flex items-start gap-4 p-5 bg-white border-2 border-slate-200 rounded-lg hover:border-blue-300 hover:shadow-lg transition-all" data-id="element-1085">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors flex-shrink-0" data-id="element-1086">
              <product.icon className="w-5 h-5" data-id="element-1087" />
            </div>
            <div className="flex-1" data-id="element-1088">
              <h4 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors flex items-center gap-2" data-id="element-1089">
                {product.name}
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1090" />
              </h4>
              <p className="text-sm text-slate-600" data-id="element-1091">{product.desc}</p>
            </div>
          </motion.a>)}
      </div>

      <h2 id="common-objects" data-id="element-1092">Common Objects</h2>
      <p data-id="element-1093">Standard objects used across multiple endpoints:</p>
      <table className="w-full text-sm my-6" data-id="element-1094">
        <thead data-id="element-1095">
          <tr className="border-b border-slate-200" data-id="element-1096">
            <th className="text-left py-2 pr-4" data-id="element-1097">Object</th>
            <th className="text-left py-2" data-id="element-1098">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-1099">
          {commonObjects.map((obj, index) => <motion.tr key={obj.name} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: index * 0.05
        }} className="border-b border-slate-100" data-id="element-1100">
              <td className="py-3 pr-4" data-id="element-1101">
                <code className="text-sm" data-id="element-1102">{obj.name}</code>
              </td>
              <td className="py-3 text-slate-600" data-id="element-1103">{obj.desc}</td>
            </motion.tr>)}
        </tbody>
      </table>

      <Callout type="tip" data-id="element-1104">
        All timestamps are ISO 8601 UTC. All IDs are opaque strings with
        resource prefixes (e.g., <code data-id="element-1105">po_</code> for payouts).
      </Callout>
    </DocsLayout>;
}